import {
  BulkMemberTransferRequestFormType,
  MemberTransferReasonDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';
import { SCHEDULED_EVENT_DATE_FORMAT } from '../dialog-forms/scheduledEventForm.util';

/** Read-only values displayed before a bulk transfer is submitted. */
export interface BulkTransferConfirmationSummary {
  memberNames: string[];
  originatingGuideName: string;
  receivingGuideName: string;
  reasonText: string;
  effectiveDateLabel?: string;
  stringDetail?: string;
  addressDetails?: string;
  comment: string;
}

interface BuildBulkTransferConfirmationSummaryParams {
  formValues: BulkMemberTransferRequestFormType;
  selectedTransferReason: MemberTransferReasonDto | null;
  membersById: Record<string, UserDto | undefined>;
  guideNameById: Record<string, string | undefined>;
}

/**
 * Resolves form ids and detail values into confirmation display labels.
 *
 * @param params - Form values, transfer reason, and lookup maps.
 * @returns Confirmation display data.
 */
export function buildBulkTransferConfirmationSummary({
  formValues,
  selectedTransferReason,
  membersById,
  guideNameById,
}: BuildBulkTransferConfirmationSummaryParams): BulkTransferConfirmationSummary {
  const summary: BulkTransferConfirmationSummary = {
    memberNames: formValues.memberIds.map(
      memberId => membersById[memberId]?.name ?? memberId
    ),
    originatingGuideName:
      guideNameById[formValues.originatingGuideId] ?? 'Unknown guide',
    receivingGuideName:
      guideNameById[formValues.receivingGuideId] ?? 'Unknown guide',
    reasonText: selectedTransferReason?.text ?? 'Unknown reason',
    comment: formValues.commentToReceivingGuide,
  };

  if (
    selectedTransferReason?.detailType === 'date' &&
    formValues.timestampDetail
  ) {
    summary.effectiveDateLabel = DateTime.fromFormat(
      formValues.timestampDetail,
      SCHEDULED_EVENT_DATE_FORMAT
    ).toFormat('MMMM d, yyyy');
  }
  if (
    selectedTransferReason?.detailType === 'string' &&
    formValues.stringDetail?.trim()
  ) {
    summary.stringDetail = formValues.stringDetail.trim();
  }
  if (
    selectedTransferReason?.detailType === 'address' &&
    formValues.numericDetailByMemberId
  ) {
    summary.addressDetails = formValues.memberIds
      .map(memberId => {
        const member = membersById[memberId];
        const addressId = formValues.numericDetailByMemberId?.[memberId];
        const address = member?.addresses?.find(
          candidate => candidate.id === addressId
        );
        return `${member?.name ?? memberId}: ${
          address
            ? [
                address.line1,
                address.line2,
                address.city,
                address.state,
                address.zip,
              ]
                .filter(Boolean)
                .join(', ')
            : 'Address unavailable'
        }`;
      })
      .join('\n');
  }

  return summary;
}
